import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ConversationGuard from './components/ConversationGuard';
import HomePage from './pages/HomePage';
import StartupSignup from './pages/StartupSignup';
import PasswordResetRequestPage from './pages/PasswordResetRequestPage';
import PasswordResetPage from './pages/PasswordResetPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import EmailVerificationPendingPage from './pages/EmailVerificationPendingPage';
import CDMOApplication from './pages/CDMOApplication';
import ContactPage from './pages/ContactPage';
import MatchingPlatformPage from './pages/MatchingPlatformPage';
import CDMODashboardPage from './pages/CDMODashboardPage';
import PricingPage from './pages/PricingPage';
import SuccessPage from './pages/SuccessPage';
import ErrorBoundary from './components/ErrorBoundry';
import CDMOProfileEditPage from './pages/CDMOProfileEditPage';
import BillingPage from './pages/BillingPage';
import { Toaster } from 'react-hot-toast';
import ReviewPage from './pages/ReviewPage';
import StartupDashboardPage from './pages/StartupDashboardPage';
import UnauthorizedAccess from './pages/UnauthorizedAccess';
import UnsubscribePage from './pages/UnsubscribePage';
import CampaignPage from './pages/CampaignPage';
import AdminAccessPage from './pages/AdminAccessPage';
import AdminCdmoImportPage from './pages/AdminCdmoImportPage';
import { CDMO_IMPORT_PATH } from './constants/cdmoImport';
import SponsorInviteLandingPage from './pages/SponsorInviteLandingPage';
import CampaignClickPage from './pages/CampaignClickPage';

function App() {
  return (
    <AuthProvider>
      <Router>
        <ErrorBoundary>
          <Layout>
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/startup-signup" element={<StartupSignup />} />
                <Route path="/claim-profile" element={<StartupSignup />} />
                <Route path="/password-reset-request" element={<PasswordResetRequestPage />} />
                <Route path="/reset-password" element={<PasswordResetPage />} />
                <Route path="/confirm-email" element={<EmailConfirmationPage />} />
                <Route path="/verify-email" element={<EmailVerificationPendingPage />} />
                <Route path="/cdmo-application" element={<CDMOApplication />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/success" element={<SuccessPage />} />
                <Route path="/unauthorized" element={<UnauthorizedAccess />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/sponsor-invite" element={<SponsorInviteLandingPage />} />
                <Route path="/campaign-click" element={<CampaignClickPage />} />
                {/* Review page route - no authentication required for direct email links */}
                <Route path="/review/:requestId/:reviewerType" element={<ReviewPage />} />
                <Route
                  path="/matchmaking"
                  element={
                    <ProtectedRoute>
                      <MatchingPlatformPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaign"
                  element={
                    <ProtectedRoute>
                      <CampaignPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin-access"
                  element={
                    <ProtectedRoute>
                      <AdminAccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={CDMO_IMPORT_PATH}
                  element={
                    <ProtectedRoute>
                      <AdminCdmoImportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaign/sponsor-invites"
                  element={<Navigate to="/campaign?tab=sponsor" replace />}
                />
                <Route
                  path="/campaign/history"
                  element={<Navigate to="/campaign?tab=history" replace />}
                />
                {/* CDMO Dashboard Routes */}
                <Route
                  path="/cdmo-dashboard"
                  element={
                    <ProtectedRoute>
                      <ConversationGuard userRole="cdmo">
                        <CDMODashboardPage />
                      </ConversationGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cdmo-dashboard/messages"
                  element={
                    <ProtectedRoute>
                      <ConversationGuard userRole="cdmo">
                        <CDMODashboardPage initialTab="messages" />
                      </ConversationGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cdmo-dashboard/messages/:conversationId"
                  element={
                    <ProtectedRoute>
                      <ConversationGuard userRole="cdmo">
                        <CDMODashboardPage initialTab="messages" />
                      </ConversationGuard>
                    </ProtectedRoute>
                  }
                />

                {/* Startup Dashboard Routes */}
                <Route
                  path="/startup-dashboard"
                  element={
                    <ProtectedRoute>
                      <ConversationGuard userRole="startup">
                        <StartupDashboardPage />
                      </ConversationGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/startup-dashboard/messages"
                  element={
                    <ProtectedRoute>
                      <ConversationGuard userRole="startup">
                        <StartupDashboardPage initialTab="messages" />
                      </ConversationGuard>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/startup-dashboard/messages/:conversationId"
                  element={
                    <ProtectedRoute>
                      <ConversationGuard userRole="startup">
                        <StartupDashboardPage initialTab="messages" />
                      </ConversationGuard>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/cdmo-profile-edit"
                  element={
                    <ProtectedRoute>
                      <CDMOProfileEditPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/billing-dashboard"
                  element={
                    <ProtectedRoute>
                      <BillingPage />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </AnimatePresence>
          </Layout>
        </ErrorBoundary>
        <Toaster position="top-center" />
      </Router>
    </AuthProvider>
  );
}

export default App;